import moment from 'moment'
import { Calendar } from 'primereact/calendar'
import React, { useEffect, useState, useRef } from 'react'
import { Alert } from '../../../common/Alert'
import { AUTOCOMPLETE_TYPE, FORM_FIELDS, NOTIFICATION_MESSAGE_TYPE } from '../../../constants'
import SessionKeys from '../../../constants/sessionKeys'
import { getAirportsByCode } from '../../../services/searchPanelServices'
import getCurrentUser from '../../../utils/getCurrentUser'
import {
	getBase64Encoded,
	setToSessionStorage,
	getFromLocalStorage,
	setToLocalStorage,
} from '../../../utils/StorageUtils'
import AirportAutocomplete from '../../AirportAutocomplete'
import './ListSLRs.scss'
import { useTranslation } from 'react-i18next'
import { addError, pushEventBookingSearch } from '../../../utils/analytics'
import LoadingIndicator from '../../LoadingIndicator'
import CommonInput from '../../../common/Resusable/CommonInput'
import CALENDAR_LOCALES from '../../../common/Calendar/calendarLocales'
import sortIcon from '../../../assets/images/sortIcon.svg'
import sortDownIcon from '../../../assets/images/sortIconDown.svg'
import sortUpIcon from '../../../assets/images/sortIconUp.svg'
import PaginatorComponent from '../../Pagination/PaginatorComponent'
import { Divider } from '../../../common/Divider'
import { SecondaryButton } from '../../../common/Buttons'
import { useHistory, useLocation } from 'react-router-dom'
import Messages from '../../../constants/Messages'
import { isEnterpriseOrGsaUser, getSLRListRequest, getWeightUnitValue, getStatusLabelKey, getSlrFallbackStation } from '../SLRUtils'
import { getSLRListBookings } from '../../../services/slrService'
import { getShipmentDetailsByCode } from '../../../services/transportationPlanServices'

const ListSLRs = ({ gsaAgentDetails }) => {
	const location = useLocation()
	const { t, i18n } = useTranslation(['labels', 'errors'])
	const isRTL = i18n.language === 'ar' || i18n.dir() === 'rtl';
	const calendarLocale = CALENDAR_LOCALES[i18n.language] || CALENDAR_LOCALES.en
	const dateFormat = i18n.language === 'zh' ? 'yy-mm月-dd日' : 'dd M yy';
	const { agentDetails } = gsaAgentDetails

	const [selectedOrigin, setSelectedOrigin] = useState(null)
	const [selectedDestination, setSelectedDestination] = useState(null)

	const [error, setError] = useState(null)

	const [notificationType, setNotificationType] = useState(null)
	const [notificationMessage, setNotificationMessage] = useState('')

	const [isLoading, setIsLoading] = useState(false)
	const [bookingFromFocused, setBookingFromFocused] = useState(false);

	const history = useHistory()

	const [isGsaUser] = useState(() => isEnterpriseOrGsaUser())

	const initialState = {
		origin: selectedOrigin,
		destination: selectedDestination,
		fromBookingDate: null,
		slrNumber: '',
		customerCode: '',
		shipmentDescription: '',
	}

	// API Integration State
	const [slrList, setSlrList] = useState([])
	const [paginationData, setPaginationData] = useState({
		currentPageNumber: 1,
		totalRecordCount: 0,
		pageSize: 10,
		totalPages: 0,
	})
	const [slrSortField, setSlrSortField] = useState({ sortBy: '', sortOrder: '' })
	const shipmentInfoCache = useRef(new Map());

	const [bookingSearchFormData, setBookingSearchFormData] =
		useState(initialState)

	useEffect(() => {
		if (location.state && location.state.slrFetchFailedError) {
			setNotificationMessage('ER076')
			setNotificationType(NOTIFICATION_MESSAGE_TYPE.ERROR)
			window.scrollTo(0, 0)

			let state = { ...location.state }
			delete state.slrFetchFailedError
			history.replace({ ...location, state })
		}
	}, [history, location])

	// Initial load with default origin and customer code
	useEffect(() => {
		window.scrollTo(0, 0)
		setIsLoading(true)

		// Restore list state when navigating back from the view screen
		if (location.state?.retainFormData) {
			const storedData = getFromLocalStorage(SessionKeys.SLR_LIST_DETAILS)
			if (storedData) {
				const parsedData = JSON.parse(storedData)
				const {
					bookingSearchFormData: storedFormData,
					selectedOrigin: storedOrigin,
					selectedDestination: storedDestination,
					slrSortField: storedSortField,
					paginationData: storedPaginationData,
				} = parsedData

				setSelectedOrigin(storedOrigin)
				setSelectedDestination(storedDestination)
				setBookingSearchFormData(storedFormData)
				setSlrSortField(storedSortField)

				let state = { ...location.state }
				delete state.retainFormData
				history.replace({ ...location, state })

				fetchSLRList(storedFormData, storedPaginationData?.currentPageNumber || 1, storedSortField)
				return
			}
		}

		const userDetails = getCurrentUser();
		const fallbackStation = getSlrFallbackStation()
		let defaultAirport = userDetails && userDetails.defaultAirportCode ? userDetails.defaultAirportCode : userDetails?.stationCode
		let defaultCustomerCode = location.state?.agentCode || userDetails?.customerCode || userDetails?.agentCode;
		if (agentDetails?.agentDefaultAirportCode) {
			defaultAirport = agentDetails && agentDetails.agentDefaultAirportCode ? agentDetails.agentDefaultAirportCode : agentDetails.agentStationCode;
		}
		if (fallbackStation) {
			defaultAirport = fallbackStation
		}
		if (!isGsaUser || location.state?.agentCode) {
			setBookingSearchFormData((prev) => ({
				...prev,
				customerCode: defaultCustomerCode,
			}));
		}
		if (location.state?.agentCode) {
			let state = { ...location.state }
			delete state.agentCode
			history.replace({ ...location, state })
		}
		if ((userDetails || fallbackStation) && defaultAirport && !selectedOrigin) {
			getAirportsByCode(defaultAirport)
				.then((defaultOrigin) => {
					setSelectedOrigin(defaultOrigin)
					// Initial load with default origin and populated customer code
					const initialFormData = {
						...bookingSearchFormData,
						origin: defaultOrigin,
						customerCode: defaultCustomerCode,
					}
					fetchSLRList(initialFormData, 1, {})
				})
				.catch((error) => {
					console.log(error)
					addError('LOADING_ERROR', error)
					setIsLoading(false)
				})
		} else {
			setIsLoading(false)
			setNotificationMessage(Messages.PROCESS_REQUEST_FAILED)
			setNotificationType(NOTIFICATION_MESSAGE_TYPE.ERROR)
		}
	}, [])


	useEffect(() => {
		setBookingSearchFormData((prev) => ({
			...prev,
			origin: selectedOrigin,
		}))
	}, [selectedOrigin])

	useEffect(() => {
		setBookingSearchFormData((prev) => ({
			...prev,
			destination: selectedDestination,
		}))
	}, [selectedDestination])

	const clearError = () => { }

	/**
	 * Enriches SLR list with commodity details and updates state
	 */
	const setSlrListAndPaginationData = async (response) => {
		if (response?.slrList) {
			// Extract unique commodities to avoid duplicate API calls
			const uniqueCommodities = [...new Set(
				response.slrList
					.map(slr => slr.commodity)
					.filter(Boolean)
			)]

			// Fetch all unique commodities in parallel (each only once)
			const commodityMap = new Map()
			if (uniqueCommodities.length > 0) {
				const commodityDataPromises = uniqueCommodities.map(commodity => {
					// Check cache first to avoid unnecessary API calls
					const cachedData = shipmentInfoCache.current.get(commodity)
					if (cachedData) {
						return Promise.resolve({ commodity, data: cachedData })
					}
					return getShipmentDetailsByCode(commodity)
						.then(response => ({ commodity, data: response?.data }))
						.catch(error => {
							console.log('Error fetching commodity details:', commodity, error)
							return { commodity, data: null }
						})
				})
				const results = await Promise.all(commodityDataPromises)
				results.forEach(({ commodity, data }) => {
					if (data) {
						shipmentInfoCache.current.set(commodity, data)
					}
					commodityMap.set(commodity, data)
				})
			}

			// Enrich SLR list with commodity details from map
			const enrichedList = response.slrList.map(slr => ({
				...slr,
				commodityDetails: slr.commodity ? commodityMap.get(slr.commodity) || null : null,
			}))

			setSlrList(enrichedList)
			setPaginationData({
				currentPageNumber: response.pageNumber || 1,
				totalRecordCount: response.totalRecordCount || 0,
				pageSize: response.pageSize || 10,
				totalPages: response.totalPages || 0,
			})
		} else {
			setSlrList([])
			setPaginationData({
				currentPageNumber: 1,
				totalRecordCount: 0,
				pageSize: 10,
				totalPages: 0,
			})
		}
	}

	/**
	 * Fetches SLR list from API and updates state
	 */
	const fetchSLRList = async (formData, pageNumber, sortData) => {
		try {
			setIsLoading(true)
			const requestBody = getSLRListRequest(formData, pageNumber, sortData, isGsaUser)
			const response = await getSLRListBookings(requestBody)
			if (response?.status === 200) {
				await setSlrListAndPaginationData(response.data)
			} else {
				throw new Error('Failed to fetch SLR list')
			}
		} catch (error) {
			console.error('Error fetching SLR list:', error)
			addError('SHIPMENTS_LIST_ERROR', error)
			setNotificationMessage(Messages.PROCESS_REQUEST_FAILED)
			setNotificationType(NOTIFICATION_MESSAGE_TYPE.ERROR)
			setSlrList([])
		} finally {
			setIsLoading(false)
		}
	}

	const handleSubmit = (event) => {
		event.preventDefault()
		setToSessionStorage(
			SessionKeys.MANAGE_SLR_SEARCH_INFO,
			getBase64Encoded(bookingSearchFormData)
		)
		setSlrSortField({ sortBy: '', sortOrder: '' })
		fetchSLRList(bookingSearchFormData, 1, {})
		pushEventBookingSearch(bookingSearchFormData)
	}

	const handleSetError = (key, text) => {
		error
			? setError({
				...error,
				[key]: text,
			})
			: setError({
				[key]: text,
			})
	}

	const handleAlertClose = () => {
		setNotificationMessage('')
		setNotificationType(null)
	}

	const handleViewSlrBooking = (slr) => {
		if (slr?.slrReferenceNumber) {
			setToLocalStorage(
				SessionKeys.SLR_LIST_DETAILS,
				JSON.stringify({
					bookingSearchFormData,
					selectedOrigin,
					selectedDestination,
					slrSortField,
					paginationData,
				})
			)
			history.push(
				`/slr-bookings/view/${encodeURIComponent(slr.slrReferenceNumber)}`,
				{ customerCode: slr.customerCode }
			)
		}
	}

	const getSortArrowByType = (sortDetails, type) => {
		const { sortBy, sortOrder } = sortDetails
		if (sortBy && sortBy === type) {
			switch (sortOrder) {
				case 'asc':
					return sortUpIcon
				case 'desc':
					return sortDownIcon
				default:
					return sortIcon
			}
		}
		return sortIcon
	}

	const handleSlrSort = async (field) => {
		const { sortOrder, sortBy } = slrSortField
		const isSameFieldClicked = field === sortBy
		let newSortOrder = isSameFieldClicked ? sortOrder : ''

		switch (newSortOrder) {
			case 'asc':
				newSortOrder = 'desc'
				break
			case 'desc':
				newSortOrder = ''
				break
			case '':
				newSortOrder = 'asc'
				break
			default:
				newSortOrder = ''
		}

		const newSortField = {
			sortBy: newSortOrder ? field : '',
			sortOrder: newSortOrder,
		}

		try {
			setIsLoading(true)
			const requestBody = getSLRListRequest(
				bookingSearchFormData,
				paginationData.currentPageNumber,
				newSortField,
				isGsaUser
			)
			const response = await getSLRListBookings(requestBody)

			if (response?.status === 200 && response?.data?.slrList) {
				setSlrSortField(newSortField)
				await setSlrListAndPaginationData(response.data)
			} else {
				throw new Error('Failed to fetch SLR list')
			}
		} catch (error) {
			console.log(error.message)
			addError('SHIPMENTS_LIST_ERROR', error)
			setNotificationMessage(Messages.PROCESS_REQUEST_FAILED)
			setNotificationType(NOTIFICATION_MESSAGE_TYPE.ERROR)
			setSlrList([])
		} finally {
			setIsLoading(false)
		}
	}

	const onSlrPageIndexChange = (newPageIndex) => {
		fetchSLRList(bookingSearchFormData, newPageIndex, slrSortField)
	}

	return (
		<div>
			{!isLoading && notificationMessage && notificationMessage !== '' && (
				<div className='pb-2'>
					<Alert
						showAlert={true}
						message={t(notificationMessage, { ns: 'errors' })}
						setTimeout={true}
						timeout={12000}
						onAlertClose={handleAlertClose}
						type={notificationType}
					/>
				</div>
			)}
			{isLoading ? <LoadingIndicator /> : <></>}
			<div className={`listBooking-main ${isRTL ? 'rtl' : ''}`}>
				<div className='listBooking-head-row slr-list-head-row'>
					<h3 className='listBooking-head mt-0'> {t('BOOKING.FIND_SLR_BOOKING')}</h3>
				</div>
				<div className='listBookingForm' id='list-booking'>
					<div id='basicSearch' className='booking-input-section'>
						<div className='input-row'>
							<div
								className={`form-group common-autocomplete ${error && error[FORM_FIELDS.ORIGIN] ? "red-border" : ""
									}`}
							>
								<div className="p-float-label input-wrapper">
									<AirportAutocomplete
										autoCompleteType={AUTOCOMPLETE_TYPE.ORIGIN}
										selectedOrigin={selectedOrigin}
										selectedDestination={selectedDestination}
										setSelectedOrigin={setSelectedOrigin}
										handleSetError={handleSetError}
										clearError={clearError}
										screen='MANAGE_BOOKING'
										draftCallback={() => { }}
										setNotificationMessage={setNotificationMessage}
										setNotificationType={setNotificationType}
									/>
									<label htmlFor="origin-airport">
										{t("COMMON.SHP_FROM")}
									</label>
								</div>
							</div>

							<div
								className={`form-group common-autocomplete ${error && error[FORM_FIELDS.DESTINATION]
									? "red-border"
									: ""
									}`}
							>
								<div className="p-float-label input-wrapper">
									<AirportAutocomplete
										autoCompleteType={AUTOCOMPLETE_TYPE.DESTINATION}
										selectedOrigin={selectedOrigin}
										selectedDestination={selectedDestination}
										setSelectedDestination={setSelectedDestination}
										handleSetError={handleSetError}
										clearError={clearError}
										screen='MANAGE_BOOKING'
										draftCallback={() => { }}
										setNotificationMessage={setNotificationMessage}
										setNotificationType={setNotificationType}
									/>
									<label htmlFor="destination-airport">
										{t("COMMON.SHP_TO")}
									</label>
								</div>
							</div>
						</div>
						<div className='input-row'>
							<div className='form-group'>
								<div className={`p-float-label ${bookingFromFocused ? 'custom-float-label' : ''}`}>
									<Calendar
										key={`fromBookingDate-${i18n.language}`}
										className="form-item__field"
										dateFormat={dateFormat}
										locale={calendarLocale}
										value={bookingSearchFormData.fromBookingDate ? moment(bookingSearchFormData.fromBookingDate).toDate() : null}
										onChange={(e) => {
											setBookingSearchFormData({
												...bookingSearchFormData,
												fromBookingDate: e.value,
											});
										}}
										showOtherMonths={false}
										inputId="fromBookingDate"
										onFocus={() => setBookingFromFocused(true)}
										onBlur={() => setBookingFromFocused(false)}
									/>
									<label htmlFor="fromBookingDate">{t('PERMANENT_BOOKING.SHIPPING_DATE')}</label>
								</div>
							</div>
							<div className='form-group'>
								<CommonInput
									id='slrNumber'
									name='slrNumber'
									type='text'
									label={t('BOOKING.SLR_RESULT_NUMBER')}
									value={bookingSearchFormData.slrNumber}
									onChange={(e) =>
										setBookingSearchFormData({
											...bookingSearchFormData,
											slrNumber: e.target.value,
										})
									}
								/>
							</div>
						</div>

						<div className='input-row'>
							<div className='form-group'>
								<CommonInput
									id='customerCode'
									name='customerCode'
									type='text'
									label={t('BOOKING.SLR_CUSTOMER_CODE')}
									value={bookingSearchFormData.customerCode}
									disabled={!isGsaUser}
									onChange={(e) =>
										setBookingSearchFormData({
											...bookingSearchFormData,
											customerCode: e.target.value,
										})
									}
								/>
							</div>

							<div className='form-group'>
								<CommonInput
									id='shipmentDescription'
									name='shipmentDescription'
									type='text'
									label={t('COMMON.SHIP_DES')}
									value={bookingSearchFormData.shipmentDescription}
									onChange={(e) =>
										setBookingSearchFormData({
											...bookingSearchFormData,
											shipmentDescription: e.target.value,
										})
									}
								/>
							</div>
						</div>

						<div className='shipping-input-bottom shipping-input-bottom--slr'>
							<button
								type="button"
								className="primary-button"
								onClick={handleSubmit}
							>
								{t("COMMON.SEARCH")}
							</button>
						</div>
					</div>
				</div>
			</div>
			<div id='BookingDetailsList' className={`mt-3 mt-lg-5 mb-3 ${isRTL ? 'rtl' : ''}`}>
				<h3 className='page-sub-head mb-1'>{t('COMMON.SLR_BOOKING_LIST')}</h3>
				{paginationData.totalRecordCount > 0 && (
					<div className='form-row mb-4 booking-details__total-results-display'>
						<div className='col-12 d-flex justify-content-between align-items-center'>
							<div className='booking-details-count'>
								{paginationData.totalRecordCount} {paginationData.totalRecordCount > 1 ? t('COMMON.REQUESTS') : t('COMMON.REQUEST')}
							</div>
							<div className='page-details-text'>
								{t('COMMON.SHOWING')} {slrList.length} {t('COMMON.OF')} {paginationData.totalRecordCount}
							</div>
						</div>
					</div>
				)}

				{slrList.length === 0 && paginationData.totalRecordCount === 0 && (
					<div className='no-data ml-0 mr-0'>
						{' '}
						{t('BOOKING.SLR_NO_RESULTS')}
					</div>
				)}

				{slrList.length > 0 && (
					<div className='common-list-table-scroll'>
						<table className={`common-list-table slr-list-table ${!isGsaUser ? 'no-status-column' : ''}`}>
							<tr className='table-header'>
								<th className='header-cell'>
									<span className='label-with-sort'>
										{t('BOOKING.SLR_NUMBER')}
										<img
											className='tp-sort-icon'
											src={getSortArrowByType(slrSortField, 'slrReferenceNumber')}
											onClick={() => handleSlrSort('slrReferenceNumber')}
										/>
									</span>
								</th>
								<th className='header-cell'>{t('CHOOSE_FLT.ORGIN_DESTINATION')}</th>
								<th className='header-cell'>
									<span className='label-with-sort'>
										{t('BOOKING.SLR_SUBMITTED_DATE')}
										<img
											className='tp-sort-icon'
											src={getSortArrowByType(slrSortField, 'submittedAt')}
											onClick={() => handleSlrSort('submittedAt')}
										/>
									</span>
								</th>
								{isGsaUser && (
									<th className='header-cell'>
										<span className='label-with-sort'>
											{t('COMMON.STATUS')}
											<img
												className='tp-sort-icon'
												src={getSortArrowByType(slrSortField, 'slrStatus')}
												onClick={() => handleSlrSort('slrStatus')}
											/>
										</span>
									</th>
								)}
								<th className='header-cell'>{t('COMMON.SHIPMENT_INFO')}</th>
								<th className='header-cell'>{t('BOOKING.CUSTOMER_DETAILS')}</th>
								<th className='header-cell' />
							</tr>
							<Divider coverage="100%" />
							<tbody className='table-body'>
								{slrList.map((slr, index) => {
									const shipmentInfo = slr?.commodity && slr?.product ? `${slr.commodity} / ${slr.product}` : (slr?.product || '-')
									const shortDescription = `${slr?.pieces || '-'} Pcs - ${slr?.grossWeight || '-'}${getWeightUnitValue(slr?.grossWeightUnit || 'K')}`
									
									return (
										<React.Fragment key={index}>
											<tr
												key={slr?.slrReferenceNumber || index}
												className={`body-row slr-clickable-row ${index % 2 === 0 ? 'even' : ''}`}
												onClick={() => handleViewSlrBooking(slr)}
											>
												<td className='body-cells'>{slr?.slrReferenceNumber || '-'}</td>
												<td className='body-cells'>{`${slr?.origin || '-'} - ${slr?.destination || '-'}`}</td>
												<td className='body-cells'>
													<div className='info-wrapper'>
														<div className='shipment-info'>{slr?.submittedDate || '-'}</div>
														{slr?.submittedTime && (
															<div className='awb-shortDetails'>{slr.submittedTime}</div>
														)}
													</div>
												</td>
												{isGsaUser && (
													<td className='body-cells'>
														<div className={`slr-status-pill ${slr?.slrStatus}`}>
															{t(getStatusLabelKey(slr?.slrStatus))}
														</div>
													</td>
												)}
												<td className='body-cells'>
													<div className='info-wrapper'>
														<div className='shipment-info'>{shipmentInfo}</div>
														<div className='awb-shortDetails'>{shortDescription}</div>
													</div>
												</td>
												<td className='body-cells'>
													<div className='info-wrapper'>
														<div className='shipment-info'>{slr?.customerName || '-'}</div>
														{slr?.customerCode && (
															<div className='awb-shortDetails'>{slr.customerCode}</div>
														)}
													</div>
												</td>
												<td
													className='body-cells link-color'
													onClick={() => handleViewSlrBooking(slr)}
												>
													{t('COMMON.VIEW')}
												</td>
											</tr>
											<Divider coverage="100%" />
										</React.Fragment>
									)
								})}
							</tbody>
						</table>
					</div>
				)}

				{slrList.length > 0 && paginationData.totalPages > 1 && (
					<div className='paginationWrapper'>
						<PaginatorComponent
							currentPage={paginationData.currentPageNumber}
							rowCount={paginationData.pageSize}
							totalRecords={paginationData.totalRecordCount}
							onPageIndexChange={onSlrPageIndexChange}
							isHotUpdateNeeded={true}
						/>
					</div>
				)}

				{!isGsaUser && history.location.state?.fromSlrBookingResult && (
					<SecondaryButton
						label={t('COMMON.BACK_BUTTON')}
						className='mt-5'
						onClick={() => history.push('/booking/search')}
					/>
				)}
			</div>
		</div>
	)
}

export default ListSLRs
